import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMaps } from '../utils/loadGoogleMaps';

const AU_STATE_MAP = {
  Queensland: 'QLD',
  'New South Wales': 'NSW',
  Victoria: 'VIC',
  'South Australia': 'SA',
  'Western Australia': 'WA',
  Tasmania: 'TAS',
  'Northern Territory': 'NT',
  'Australian Capital Territory': 'ACT',
};

const DEFAULT_INPUT_CLASS = 'box-border w-full   rounded-2xl border-0 bg-slate-100/80 px-4 py-3.5 text-[15px] text-slate-950 outline-none ring-1 ring-inset ring-slate-200 transition-all duration-200 placeholder:text-slate-400 hover:bg-white hover:ring-slate-300 focus:bg-white focus:ring-2 focus:ring-green-500';

const parsePlace = (place) => {
  const components = place.address_components || [];
  const get = (type) => components.find((item) => item.types.includes(type));
  const stateLong = get('administrative_area_level_1')?.long_name || '';

  return {
    address: place.formatted_address || '',
    suburb:
      get('locality')?.long_name ||
      get('postal_town')?.long_name ||
      get('sublocality_level_1')?.long_name ||
      get('administrative_area_level_2')?.long_name ||
      '',
    state: AU_STATE_MAP[stateLong] || get('administrative_area_level_1')?.short_name || '',
    postcode: get('postal_code')?.long_name || '',
    lat: place.geometry?.location?.lat() ?? null,
    lng: place.geometry?.location?.lng() ?? null,
  };
};

const LocationAutocomplete = ({
  apiKey,
  onSelect,
  onInputChange,
  placeholder = 'Enter your suburb or address…',
  defaultValue = '',
  value,
  className = '',
  errorClassName = '',
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const [internalValue, setInternalValue] = useState(value ?? defaultValue);
  const [error, setError] = useState('');

  useEffect(() => {
    if (value !== undefined) setInternalValue(value || '');
  }, [value]);

  useEffect(() => {
    if (!apiKey) {
      setError('Location search is unavailable (missing Maps key).');
      return undefined;
    }

    let cancelled = false;
    loadGoogleMaps(apiKey)
      .then((maps) => {
        if (cancelled || !inputRef.current) return;
        autocompleteRef.current = new maps.places.Autocomplete(inputRef.current, {
          fields: ['address_components', 'formatted_address', 'geometry'],
          componentRestrictions: { country: 'au' },
        });
        autocompleteRef.current.addListener('place_changed', () => {
          const place = autocompleteRef.current.getPlace();
          if (!place.geometry) return;
          const parsed = parsePlace(place);
          setInternalValue(parsed.address);
          onSelect?.(parsed);
        });
      })
      .catch(() => {
        if (!cancelled) setError('Could not load location search.');
      });

    return () => {
      cancelled = true;
    };
  }, [apiKey, onSelect]);

  const handleChange = (event) => {
    const next = event.target.value;
    setInternalValue(next);
    onInputChange?.(next);
  };

  return (
    <div className="min-w-0 w-full">
      <input
        ref={inputRef}
        value={internalValue}
        onChange={handleChange}
        placeholder={placeholder}
        className={className || DEFAULT_INPUT_CLASS}
        autoComplete="off"
      />
      {error && <div className={errorClassName || 'mt-2 break-words text-sm font-medium text-red-600'}>{error}</div>}
    </div>
  );
};

export default LocationAutocomplete;
